import React, { useEffect } from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import ShimmerHalo from '../shared/ShimmerHalo';
import { BadgeShieldIcon, CheckIcon, CloseIcon, LockIcon } from '../shared/RelicVaultIcons';
import { useRelicVaultLayout } from '../shared/useRelicVaultLayout';
import {
  COLORS,
  bodyFont,
  em,
  headingFont,
  modalBackdrop,
  modalSurface,
} from '../shared/theme';
import { iconGlowFor } from 'utils/relicVault/colorUtils';
import { IS_WEB } from 'utils/relicVault/webStyle';

import { TYPE } from 'style/typography';

const CARD_WIDTH = 380;
const ART_SIZE = 210;

/**
 * Badge detail popup. Like the reference, it leads with the badge itself and
 * says how to earn it; the requirement text comes straight from the badge sheet.
 */
const BadgeDetailModal = ({ catalog, badgeId, owned, onClose }) => {
  const { width, isPhone } = useRelicVaultLayout();
  // Escape closes it on the web, like its X (Sol owner-r27 Q1)
  useEffect(() => {
    if (!IS_WEB || !badgeId || typeof window === 'undefined') return undefined;
    const onKey = event => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [badgeId, onClose]);
  const badge = badgeId ? catalog.badgeById.get(badgeId) : null;
  if (!badge) return null;

  const tier = catalog.tierByKey.get(badge.rarity);
  const rarityColor = tier?.colorHex ?? '#4ADE80';
  const image = badge.imageUrl;
  const cardWidth = isPhone ? width * 0.92 : Math.min(CARD_WIDTH, width - 48);

  return (
    <View style={[StyleSheet.absoluteFill, styles.backdrop, modalBackdrop]}>
      <TouchableOpacity
        activeOpacity={1}
        style={StyleSheet.absoluteFill}
        onPress={onClose}
        accessibilityLabel="Close"
      />
      <View
        style={[styles.card, modalSurface, { width: cardWidth }, isPhone && styles.cardPhone]}>
        <TouchableOpacity
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close"
          style={styles.closeBtn}>
          <View style={styles.closeDot}>
            <CloseIcon />
          </View>
        </TouchableOpacity>

        <View
          style={[
            styles.art,
            owned && image
              ? IS_WEB
                ? { backgroundImage: iconGlowFor(rarityColor) }
                : { backgroundColor: `${rarityColor}22` }
              : { backgroundColor: 'rgba(255,255,255,0.05)' },
          ]}>
          {image ? (
            <Image
              source={{ uri: image }}
              resizeMode="contain"
              style={styles.artImage}
            />
          ) : (
            /* Still being drawn by the designer. */
            <View style={styles.artPending}>
              <BadgeShieldIcon size={40} color="rgba(201,193,240,0.5)" />
            </View>
          )}
          {!owned && !!image && (
            <View style={styles.lock}>
              <LockIcon size={34} color={COLORS.gold} strokeWidth="2.6" />
            </View>
          )}
          <ShimmerHalo
            glow={owned && image ? `${rarityColor}80` : 'rgba(255,255,255,0.25)'}
            radius={18}
            haloSize={30}
            haloSpread={5}
            replayKey={badge.badgeId}
            delay={100}
          />
        </View>

        <View style={[styles.rarityPill, { borderColor: rarityColor }]}>
          <Text style={[styles.rarityText, { color: rarityColor }]}>
            {tier?.label ?? badge.rarity} Badge
          </Text>
        </View>

        <Text style={styles.name}>{badge.name}</Text>
        <Text style={styles.category}>
          {badge.categoryLabel}
          {!!badge.guildTitle && ` · ${badge.guildTitle}`}
        </Text>

        <Text style={styles.howToLabel}>How to earn it</Text>
        <Text style={styles.requirement}>{badge.requirementText}</Text>

        {owned ? (
          <View style={[styles.status, styles.statusOwned]}>
            <CheckIcon size={12} />
            <Text style={[styles.statusText, { color: COLORS.green }]}>Earned</Text>
          </View>
        ) : (
          <View style={[styles.status, styles.statusLocked]}>
            <Text style={[styles.statusText, { color: COLORS.muted }]}>Not Earned Yet</Text>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    zIndex: 1000,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  card: {
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.14)',
    borderRadius: 24,
    paddingTop: 32,
    paddingHorizontal: 28,
    paddingBottom: 28,
    alignItems: 'center',
  },
  cardPhone: {
    paddingTop: 24,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  // a 32px X with a 44x44 press area (Sol owner-r27 Q1)
  closeBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
  },
  closeDot: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.08)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  art: {
    width: ART_SIZE,
    maxWidth: '100%',
    aspectRatio: 1,
    borderRadius: 18,
    marginBottom: 14,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  artImage: {
    width: '86%',
    height: '86%',
  },
  lock: {
    position: 'absolute',
    width: 62,
    height: 62,
    borderRadius: 999,
    backgroundColor: 'rgba(6,5,18,0.82)',
    borderWidth: 1.5,
    borderColor: 'rgba(255,216,115,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  artPending: {
    width: '42%',
    aspectRatio: 1,
    borderRadius: 999,
    backgroundColor: 'rgba(6,5,18,0.9)',
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: 'rgba(255,255,255,0.16)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rarityPill: {
    borderWidth: 2,
    borderRadius: 999,
    paddingVertical: 5,
    paddingHorizontal: 14,
    marginBottom: 10,
  },
  rarityText: {
    ...headingFont('800'),
    fontSize: TYPE.xs,
    letterSpacing: em(0.04, 11),
    textTransform: 'uppercase',
  },
  name: {
    ...headingFont('800'),
    fontSize: TYPE.xl,
    color: COLORS.textWarm,
    textAlign: 'center',
  },
  category: {
    ...bodyFont('600'),
    fontSize: TYPE.xs,
    color: COLORS.muted,
    marginTop: 6,
    textAlign: 'center',
  },
  howToLabel: {
    ...bodyFont('800'),
    fontSize: TYPE.xs,
    letterSpacing: em(0.12, 10.5),
    textTransform: 'uppercase',
    color: COLORS.dim,
    marginTop: 16,
  },
  requirement: {
    ...bodyFont('400'),
    fontSize: TYPE.sm,
    lineHeight: 21,
    color: COLORS.lilacSoft,
    marginTop: 6,
    textAlign: 'center',
  },
  status: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 18,
    paddingVertical: 7,
    paddingHorizontal: 16,
    borderRadius: 999,
    borderWidth: 1.5,
  },
  statusOwned: {
    backgroundColor: 'rgba(74,222,128,0.14)',
    borderColor: 'rgba(74,222,128,0.4)',
  },
  statusLocked: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderColor: 'rgba(255,255,255,0.12)',
  },
  statusText: {
    ...headingFont('800'),
    fontSize: TYPE.xs,
    marginLeft: 6,
  },
});

export default BadgeDetailModal;
